<template>
  <div id="page-loan-agreement">
    <div class="display-1 py-3">{{$t('LoanAgreement.title')}}</div>

    <div class="row mb-3">
      <div id="loan-amount" class="col-6 form-group">
        <div class="small text-muted">{{$t('LoanAgreement.loanAmount')}}</div>
        <strong>{{ $n(draft.loan.amount, 'currencyNoDecimals') }}</strong>
      </div>
      <div id="loan-period" class="col-6 form-group">
        <div class="small text-muted">{{$t('LoanAgreement.loanPeriod')}}</div>
        <strong>{{$tc('LoanAgreement.month', draft.loan.periodInMonths)}}</strong>
      </div>
      <div v-if="showCampaign" id="loan-monthly-payment" class="col-6 form-group">
        <div class="small text-muted">{{$t('LoanAgreement.monthlyPayments')}}</div>
        <strong>{{ $n(draft.loan.productMonthlyPayment, 'currencyNoDecimals') }}</strong>
      </div>
      <div v-else id="loan-monthly-payment" class="col-6 form-group">
        <div class="small text-muted">{{$t('LoanAgreement.monthlyPayments')}}</div>
        <strong>{{ $n(draft.loan.monthlyPayment, 'currencyNoDecimals') }}</strong>
      </div>
      <div id="loan-total-payment" class="col-6 form-group">
        <div class="small text-muted">{{$t('LoanAgreement.totalPayment')}}</div>
        <strong>{{ $n(draft.loan.amount + draft.fees.total, 'currencyNoDecimals') }}</strong>
      </div>
      <div id="total-fees" class="col-6 form-group">
        <div class="small text-muted-more">{{$t('LoanAgreement.fees')}}<span v-if="showAccessFeeDisclaimer" class="text-muted small ml-1">*</span></div>
        <strong class="text-muted small">{{$n(draft.fees.total, 'currencyNoDecimals')}}</strong>
      </div>
      <div id="loan-percents" class="col-6 form-group">
        <div class="small text-muted-more" id="percents-label">{{percentsLabel}}</div>
        <strong class="text-muted small" id="percents-value">{{percents}}</strong>
      </div>
    </div>

    <div v-if="showCampaign"
         class="card border-bold rounded d-flex align-items-center justify-content-between flex-row py-2 py-lg-3 px-3 mb-4">
      <div class="d-flex align-items-lg-center">
        <Icon icon-name="iute-pepper-outline" class="text-primary mt-0 ml-n1 mr-1"></Icon>
        <div class="font-weight-bold">{{$t('LoanAgreement.campaignMonthlyPayments')}}</div>
      </div>
      <strong class="d-flex h5 text-primary m-0" id="monthly-payment-campaign">{{
          $n(draft.loan.monthlyPayment, 'currencyNoDecimals')
        }}</strong>
    </div>

    <div v-if="showAccessFeeDisclaimer" id="access-fee-disclaimer" class="small text-muted mb-1">
      <span class="mr-1">*</span> <span v-html="$t('LoanFormPage.accessFeeDisclaimer', { 'amount': $n(draft.fees.accessFee, 'currencyNoDecimals') })"></span>
    </div>
    <p class="small text-muted">{{$t('LoanAgreement.notice')}}</p>

    <template v-if="isNewCustomer">
      <div class="custom-control custom-switch mb-3" id="marketing-allowed-checkbox">
        <input type="checkbox" class="custom-control-input" v-model="application.marketingAllowed" id="agreementCheckbox">
        <i18n path="LoanAgreement.newsAgreement" tag="label" class="custom-control-label" for="agreementCheckbox"
              id="loan-agreement-marketing-label">
          <template v-slot:action v-if="showMarketingConsentLink">
            <router-link id="loan-marketing-consent-link" :to="{ name: 'marketing-consent' }">{{ $t('LoanAgreement.newsAgreementLink') }}</router-link>
          </template>
        </i18n>
      </div>
    </template>

    <div class="custom-control custom-switch mb-3" id="loan-agreement-pep">
      <input type="checkbox" class="custom-control-input" v-model="application.notPep" id="loan-agreement-pep-agreementCheckbox">
      <i18n path="LoanAgreement.pep" tag="label" class="custom-control-label" for="loan-agreement-pep-agreementCheckbox"
            id="loan-agreement-pep-label">
        <template v-slot:action v-if="showPepConsentLink">
          <router-link id="loan-pep-consent-link" :to="{ name: 'pep-consent' }">{{ $t('LoanAgreement.pepConsentLink') }}</router-link>
        </template>
      </i18n>
    </div>

    <template v-if="showConsent">
      <div class="custom-control custom-switch mb-3" id="loan-credit-registry-consent">
        <input type="checkbox" class="custom-control-input" v-model="application.creditRegistryConsent" id="loan-credit-registry-consent-agreementCheckbox">
        <i18n path="LoanAgreement.creditRegistryConsent" tag="label" class="custom-control-label" for="loan-credit-registry-consent-agreementCheckbox">
          <template v-slot:action>
            <router-link id="loan-credit-registry-consent-link" :to="{ name: 'credit-registry-consent' }">
              {{ $t('LoanAgreement.consent') }}
            </router-link>
          </template>
        </i18n>
      </div>
    </template>

    <template v-if="!hideTermsList">
      <LoanTermsList>
        <template v-slot:before v-if="isComfort">
          <router-link id="comfort-loan-precontract-link"
                       :to="{ name: 'receive-precontract' }"
                       class="list-group-item list-group-item-action list-group-item-with-icon">
            <span>{{  $t('LoanAgreement.preContract') }}</span>
            <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
          </router-link>
        </template>
      </LoanTermsList>
      <p class="small" id="loan-agreement-label">{{$t(`LoanAgreement.autoAgreement.${agreementLabel}`)}}</p>
    </template>
    <template v-else>
      <p class="small" id="loan-agreement-label">
        <i18n path="LoanAgreement.personalDataProcessingConsent">
          <template v-slot:action>
            <router-link id="personal-data-processing-consent-link" :to="{ name: 'privacy' }">{{ $t('LoanAgreement.actionPrivacyPolicy') }}</router-link>
          </template>
        </i18n>
      </p>
      <p class="small" id="loan-agreement-label">
        <i18n :path="`LoanAgreement.autoAgreement.${agreementLabel}`">
          <template v-slot:action1>
            <router-link id="loan-credit-registry-consent-link" :to="{ name: 'general-terms' }">{{ $t('LoanAgreement.autoAgreement.basic_action1') }}</router-link>
          </template>
          <template v-slot:action2>
            <router-link id="personal-data-processing-consent-link2" :to="{ name: 'privacy' }">{{ $t('LoanAgreement.autoAgreement.basic_action2') }}</router-link>
          </template>
        </i18n>
      </p>
    </template>
    <div>
      <button id="button-agree-and-submit" class="btn btn-lg btn-primary btn-block" :disabled="!canBeSubmitted" @click="agreeAndProceedWithSigning">
        {{$t('LoanAgreement.agreeAndSubmit')}}
      </button>
    </div>
  </div>
</template>

<script>
import LoanTermsList from '@shared/components/LoanTerms'
import Icon from '@shared/components/Icon'

export default {
    name: 'LoanAgreement',
    components: { LoanTermsList, Icon },
    props: [
      'application', 'draft', 'isNewCustomer',
      'includeCreditRegistryConsent', 'showPepConsentLink', 'showMarketingConsentLink',
      'showEir', 'showApr', 'showCampaign', 'hideTermsList'
    ],
    computed: {
      percentsLabel() {
        const percents = []
        if (this.showApr) {
          percents.push(this.$t('LoanAgreement.apr'))
        }
        if (this.showEir) {
          percents.push(this.$t('LoanAgreement.eir'))
        }
        return percents.join(' / ')
      },
      percents() {
        const percents = []
        if (this.showApr) {
          percents.push(`${this.$n(this.draft.loan.apr, 'percent')}%`)
        }
        if (this.showEir) {
          percents.push(`${this.$n(this.draft.loan.interestRate, 'percent')}%`)
        }
        return percents.join(' / ')
      },
      showConsent() {
        return !this.isComfort && this.includeCreditRegistryConsent
      },
      isComfort() {
        return this.application.loan.type === 'COMFORT'
      },
      canBeSubmitted() {
        return !this.showConsent || this.application.creditRegistryConsent
      },
      agreementLabel() {
        return this.application.loan.type === 'COMFORT' ? 'COMFORT' : 'BASIC'
      },
      showAccessFeeDisclaimer() {
        return this.draft.fees.accessFee > 0
      }
    },
    methods: {
      agreeAndProceedWithSigning() {
        this.application.dataProcessingConsent = true
        this.$emit('submit-loan-application')
      }
    }
  }
</script>
